import { useTranslations } from "next-intl";
import { CategoryItems } from "@/app/(shop)/services/[slug]/components/categoryItems";
import { RecommendedItemsCategory } from '@/components/menu/recommendedItemsCategory';
import { Category } from "@/dto/category";
import { Item, Layout } from "@/dto/item";
import { Menu } from "@/dto/menu";
import { UNCATEGORISED_CATEGORY_ID } from "@/utils/constants";
import {
  getGiftCardCategory,
  GIFT_CARD_CATEGORY_ID,
} from "@/utils/giftCardUtils";
import { useMenuSearch } from "@/utils/hooks/useMenuSearch";
import { SearchResultsHeader } from "../search/searchResultsHeader";

interface MenuCategoriesListProps {
  menu: Menu | null;
  merchantName?: string;
  itemTemplate?: string;
  query: string | undefined;
  baseRoute?: string;
  queryParams?: string;
  uncategorizedLabel: string;
  serviceCategories?: Category[];
  retailCategories?: Category[];
  getItemHref?: (item: Item) => string;
  uncategorizedItemFilter?: (item: Item) => boolean;
  layout?: Layout;
}

export const MenuCategoriesList = ({
  menu,
  merchantName,
  itemTemplate,
  query,
  baseRoute,
  queryParams,
  uncategorizedLabel,
  serviceCategories,
  retailCategories,
  getItemHref,
  uncategorizedItemFilter,
  layout = "default",
}: MenuCategoriesListProps) => {
  const tGiftCard = useTranslations("GiftCard");
  const localizedGiftCardCategory = getGiftCardCategory(tGiftCard);

  const categoryOverride = serviceCategories ?? retailCategories;

  const sortedCategories = categoryOverride
    ? categoryOverride
        .filter(
          (cat: any) =>
            cat.id !== GIFT_CARD_CATEGORY_ID &&
            cat.id !== UNCATEGORISED_CATEGORY_ID
        )
        .sort(
          (a: any, b: any) =>
            (a as { sortOrder: number }).sortOrder -
            (b as { sortOrder: number }).sortOrder
        )
    : menu &&
      Object.values(menu.categories)
        .sort(
          (a: any, b: any) =>
            (a as { sortOrder: number }).sortOrder -
            (b as { sortOrder: number }).sortOrder
        )
        .filter((category: any) => category.id !== GIFT_CARD_CATEGORY_ID);


  const getGiftCardEntry = () => {
    if (serviceCategories) return undefined;
    if (retailCategories) {
      return retailCategories.find((cat) => cat.id === GIFT_CARD_CATEGORY_ID);
    }
    return menu?.categories[GIFT_CARD_CATEGORY_ID];
  };
  const giftCardCat = getGiftCardEntry();

  const { searchItemCount, itemsMap } = useMenuSearch({
    items: menu?.items,
    query,
    type: serviceCategories ? "SERVICE" : "REGULAR",
  });

  const uncategorizedItems = menu?.items
    .filter(
      (item: any) =>
        item.categoryIds.length === 0 &&
        (uncategorizedItemFilter ? uncategorizedItemFilter(item) : true)
    )
    .map((item) => item.id);

  const markers = menu?.markers;

  return (
    <>
      {query && (
        <div className={`mt-9 mb-8`}>
          <SearchResultsHeader totalItemCount={searchItemCount} query={query} />
        </div>
      )}
      <RecommendedItemsCategory 
        {...{
          menu,
          itemTemplate,
          itemsMap: itemsMap,
          query: query || "",
          baseRoute,
          queryParams,
          getItemHref,
          markers,
        }}
        layout={layout}
      />
      {sortedCategories?.map((category: any) => (
        <CategoryItems
          {...{
            category,
            itemTemplate,
            itemsMap,
            query: query || "",
            baseRoute,
            queryParams,
            getItemHref,
            markers,
          }}
          merchantName={merchantName}
          key={category.id}
          layout={layout}
        />
      ))}
      {uncategorizedItems && uncategorizedItems.length > 0 && (
        <CategoryItems
          {...{
            category: {
              id: "Uncategorized",
              name: uncategorizedLabel,
              items: uncategorizedItems,
              sortOrder: 0,
            },
            itemTemplate,
            itemsMap,
            query: query || "",
            baseRoute,
            queryParams,
            getItemHref,
            markers,
          }}
          layout={layout}
        />
      )}
      {giftCardCat && (
        <CategoryItems
          {...{
            category: localizedGiftCardCategory,
            itemTemplate,
            itemsMap,
            query: query || "",
            baseRoute,
            queryParams,
            getItemHref,
            markers
          }}
          merchantName={merchantName}
          layout={layout}
        />
      )}
    </>
  );
};
